import { MutationTree } from 'vuex'
import { SmartMarketingState } from '@/store/smartMarketing/index'
import { CredentialsType, EmmaDefaults } from '@/api/types'
import { Credentials } from '@/api/model/credentials'
import { CompanyAPI, UserAPI } from '@/api/backend-client'
import { Company } from '@/api/model/company'
import { User } from '@/api/model/user'
import { RealworksKenmerk } from '@/api/model/realworks-kenmerk'

export const mutations: MutationTree<SmartMarketingState> = {
    load(state: SmartMarketingState): void {
        state.loading = true
        state.error = null
    },

    loadSuccess(state: SmartMarketingState, credentials: Credentials): void {
        for (const key in credentials) {
            if (state.hasOwnProperty(key)) {
                state[key] = credentials[key]
            }
        }

        state.loading = false
        state.error = null
        state.company = CompanyAPI.currentCompany
        state.updatedBy = UserAPI.currentUser

        // toggle enabled if either field has a value
        // we do this to ensure both fields are shown
        state.eventTrackingEnabled = state.eventTrackingAccountId !== null
            || state.eventTrackingKey !== null
            || false
    },

    loadFailure(state: SmartMarketingState, error: string): void {
        state.loading = false
        state.error = error
    },

    save(state: SmartMarketingState): void {
        state.saving = true
        state.error = null
    },

    saveSuccess(state: SmartMarketingState): void {
        state.saving = false
        state.error = null
    },

    saveFailure(state: SmartMarketingState, error: string): void {
        state.saving = false
        state.error = error
    },

    validate(state: SmartMarketingState): void {
        state.valid = false
        state.validating = true
    },

    validateSuccess(state: SmartMarketingState, valid: boolean): void {
        state.valid = valid
        state.validating = false
    },

    validateFailure(state: SmartMarketingState, error: string): void {
        state.valid = false
        state.validating = false
        state.error = error
    },

    activate(state: SmartMarketingState): void {
        state.activating = true
    },

    activateSuccess(state: SmartMarketingState): void {
        state.activating = false
    },

    activateFailure(state: SmartMarketingState, error: string): void {
        state.activating = false
        state.error = error
    },

    updateContactCount(state: SmartMarketingState): void {
        state.loadingContactsCount = true
    },

    updateContactCountSuccess(state: SmartMarketingState, contactCount: number): void {
        state.loadingContactsCount = false
        state.contactsCount = contactCount
    },

    updateContactCountFailure(state: SmartMarketingState, error: string): void {
        state.loadingContactsCount = false
        state.error = error
    },

    updateRealworksKenmerken(state: SmartMarketingState): void {
        state.loadingRealworksKenmerken = true
    },

    updateRealworksKenmerkenSuccess(state: SmartMarketingState, realworksKenmerken: RealworksKenmerk[]): void {
        state.loadingRealworksKenmerken = false
        state.realworksKenmerken = realworksKenmerken
    },

    updateRealworksKenmerkenFailure(state: SmartMarketingState, error: string): void {
        state.loadingRealworksKenmerken = false
        state.error = error
    },

    updateRealworksRelatietypes(state: SmartMarketingState): void {
        state.loadingRealworksRelatietypes = true
    },

    updateRealworksRelatietypesSuccess(state: SmartMarketingState, realworksRelatietypes: string[]): void {
        state.loadingRealworksRelatietypes = false
        state.realworksRelatietypes = realworksRelatietypes
    },

    updateRealworksRelatietypesFailure(state: SmartMarketingState, error: string): void {
        state.loadingRealworksRelatietypes = false
        state.error = error
    },

    updateRealworksAgendatypes(state: SmartMarketingState): void {
        state.loadingRealworksAgendatypes = true
    },

    updateRealworksAgendatypesSuccess(state: SmartMarketingState, realworksAgendatypes: string[]): void {
        state.loadingRealworksAgendatypes = false
        state.realworksAgendatypes = realworksAgendatypes

        // Enable agenda feature if we have results.
        if (state.syncedRealworksAgendatypes.length > 0) {
            state.realworksAgendaEnabled = true
        }
    },

    updateRealworksAgendatypesFailure(state: SmartMarketingState, error: string): void {
        state.loadingRealworksAgendatypes = false
        state.error = error
    },

    updateRealworksTaaktypes(state: SmartMarketingState): void {
        state.loadingRealworksTaaktypes = true
    },

    updateRealworksTaaktypesSuccess(state: SmartMarketingState, realworksTaaktypes: string[]): void {
        state.loadingRealworksTaaktypes = false
        state.realworksTaaktypes = realworksTaaktypes

        // Enable taak feature if we have results.
        if (state.syncedRealworksTaaktypes.length > 0) {
            state.realworksTaakEnabled = true
        }
    },

    updateRealworksTaaktypesFailure(state: SmartMarketingState, error: string): void {
        state.loadingRealworksTaaktypes = false
        state.error = error
    },

    updateRealworksAgendaEnabled(state: SmartMarketingState, enabled: boolean): void {
        state.realworksAgendaEnabled = enabled
    },

    updateRealworksTaakEnabled(state: SmartMarketingState, enabled: boolean): void {
        state.realworksTaakEnabled = enabled
    },

    /* Credentials */

    updateCompany(state: SmartMarketingState, company: Company): void {
        state.company = company
    },

    updateId(state: SmartMarketingState, id: string): void {
        state.id = id
    },

    updateEnabled(state: SmartMarketingState, enabled: boolean): void {
        state.enabled = enabled
    },

    updateCreatedAt(state: SmartMarketingState, createdAt: Date): void {
        state.createdAt = createdAt
    },

    updateCreatedBy(state: SmartMarketingState, createdBy: User): void {
        state.createdBy = createdBy
    },

    updateUpdatedAt(state: SmartMarketingState, updatedAt: Date): void {
        state.updatedAt = updatedAt
    },

    updateUpdatedBy(state: SmartMarketingState, updatedBy: User): void {
        state.updatedBy = updatedBy
    },

    updateCredentialsType(state: SmartMarketingState, credentialsType: CredentialsType): void {
        state.credentialsType = credentialsType
    },

    /* Shared */

    updateIncludeInternalContacts(state: SmartMarketingState, includeInternalContacts: boolean): void {
        state.includeInternalContacts = includeInternalContacts
    },

    updateIncludeExternalContacts(state: SmartMarketingState, includeExternalContacts: boolean): void {
        state.includeExternalContacts = includeExternalContacts
    },

    updateIncludeRealworksRelaties(state: SmartMarketingState, includeRealworksRelaties: boolean): void {
        state.includeRealworksRelaties = includeRealworksRelaties
    },

    updateSyncedRealworksKenmerken(state: SmartMarketingState, syncedRealworksKenmerken: string[]): void {
        state.syncedRealworksKenmerken = syncedRealworksKenmerken
    },

    updateExcludedRealworksKenmerken(state: SmartMarketingState, excludedRealworksKenmerken: string[]): void {
        state.excludedRealworksKenmerken = excludedRealworksKenmerken
    },

    updateSyncedRealworksRelatietypes(state: SmartMarketingState, syncedRealworksRelatietypes: string[]): void {
        state.syncedRealworksRelatietypes = syncedRealworksRelatietypes
    },

    updateSyncedRealworksStatus(state: SmartMarketingState, syncedRealworksStatus: boolean|null): void {
        state.syncedRealworksStatus = syncedRealworksStatus
    },

    updateUpdatedFrom(state: SmartMarketingState, updatedFrom: Date): void {
        state.updatedFrom = updatedFrom
    },

    updateUpdatedUntil(state: SmartMarketingState, updatedUntil: Date): void {
        state.updatedUntil = updatedUntil
    },

    updateApiKey(state: SmartMarketingState, apiKey: string): void {
        state.apiKey = apiKey
    },

    updateListId(state: SmartMarketingState, listId: string): void {
        state.listId = listId
    },

    /* ActiveCampaignCredentials */

    updateApiUrl(state: SmartMarketingState, apiUrl: string): void {
        state.apiUrl = apiUrl
    },

    updateEventTrackingEnabled(state: SmartMarketingState, eventTrackingEnabled: boolean): void {
        state.eventTrackingEnabled = eventTrackingEnabled

        // clear fields if disabled
        if (eventTrackingEnabled === false) {
            state.eventTrackingAccountId = null
            state.eventTrackingKey = null
        }
    },

    updateEventTrackingAccountId(state: SmartMarketingState, eventTrackingAccountId: string): void {
        state.eventTrackingAccountId = eventTrackingAccountId
    },

    updateEventTrackingKey(state: SmartMarketingState, eventTrackingKey: string): void {
        state.eventTrackingKey = eventTrackingKey
    },

    updateDefaultTeams(state: SmartMarketingState, defaultTeams: string[]): void {
        state.defaultTeams = defaultTeams
    },

    updateDefaultStatus(state: SmartMarketingState, defaultStatus: string): void {
        state.defaultStatus = defaultStatus
    },

    updateSyncedRealworksAgendatypes(state: SmartMarketingState, syncedRealworksAgendatypes: string[]): void {
        state.syncedRealworksAgendatypes = syncedRealworksAgendatypes
    },

    updateSyncedRealworksTaaktypes(state: SmartMarketingState, syncedRealworksTaaktypes: string[]): void {
        state.syncedRealworksTaaktypes = syncedRealworksTaaktypes
    },

    updateAgendaListId(state: SmartMarketingState, agendaListId: string|null): void {
        state.agendaListId = agendaListId
    },

    updateTaakListId(state: SmartMarketingState, taakListId: string|null): void {
        state.taakListId = taakListId
    },

    updateViewingListId(state: SmartMarketingState, viewingListId: string|null): void {
        state.viewingListId = viewingListId
    },

    updateEmmaListId(state: SmartMarketingState, emmaListId: string|null): void {
        state.emmaListId = emmaListId
    },

    updateEmmaDefaults(state: SmartMarketingState, emmaDefaults: EmmaDefaults): void {
        state.emmaDefaults = emmaDefaults
    },

    /* MailchimpCredentials */

    updateServer(state: SmartMarketingState, server: string): void {
        state.server = server
    },
}
